import { Progress, Stack, Text } from '@repletoryreach/sdk-ui';
import { passwordRules, passwordStrength } from '../../lib/password';
import { useT } from '../../i18n/I18nProvider';

export interface PasswordRulesProps {
  /** The candidate password. */
  value: string;
  /** Hide the meter and show only the checklist. */
  withMeter?: boolean;
}

/**
 * Live rule checklist + strength meter, shared by signup, reset-password and
 * anywhere else that sets a password.
 *
 * Every one of those surfaces previously stated the rules differently, or not
 * at all, and none of them checked anything client-side: so a weak password
 * cost a round trip and came back as untranslated framework English
 * (_UI_improvements/tenant/auth-signup.md §3, auth-reset-password.md §4 item 1).
 */
export function PasswordRules({ value, withMeter = true }: PasswordRulesProps) {
  const t = useT();
  const rules = passwordRules(value);
  const strength = passwordStrength(value);

  return (
    <Stack gap={6}>
      {withMeter && (
        <Progress
          value={strength}
          size="xs"
          radius="xl"
          aria-label={t('password.strength.label')}
        />
      )}
      <ul className="rr-auth__rules">
        {rules.map((rule) => (
          <li key={rule.key} className={`rr-auth__rule${rule.met ? ' is-met' : ''}`}>
            <span aria-hidden>{rule.met ? '✓' : '○'}</span>
            <Text role="caption">{t(rule.key)}</Text>
          </li>
        ))}
      </ul>
    </Stack>
  );
}
